import { Tooltip, Button } from 'uikit';
import type { Direction, Position, TooltipPropStatus } from 'uikit';
import { getElementCoordinatesBySelector } from 'utils/dom';

import type { CSSProperties, ReactNode } from 'react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import Style from './TooltipWrapper.module.css';

interface ITooltipWrapper {
    message: string | ReactNode;
    isHidden?: boolean;
    direction?: Direction;
    positionElementClassName?: string;
    isConfirm?: boolean;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    size?: 's' | 'm' | 'l';
    className?: string;
    toolTipClassName?: string;
    style?: CSSProperties;
    toolTipStyle?: CSSProperties;
    isInteractive?: boolean;
    isPermanentlyVisible?: boolean;
    isInlineTable?: boolean;
    status?: TooltipPropStatus;
    wrapperRef?: React.RefObject<HTMLDivElement>;
}

export const TooltipWrapper: React.FC<ITooltipWrapper> = (props) => {
    const {
        children,
        message,
        isHidden,
        positionElementClassName,
        isConfirm,
        confirmLabel = 'Выполнить',
        cancelLabel = 'Отменить',
        onConfirm,
        onCancel,
        size = 'l',
        className,
        style,
        direction = 'upCenter',
        isInteractive = true,
        toolTipClassName,
        toolTipStyle,
        isPermanentlyVisible,
        isInlineTable,
        status,
        wrapperRef,
    } = props;
    const localWrapperRef = useRef<HTMLDivElement>(null);
    const ref = wrapperRef || localWrapperRef;

    const [tooltipPosition, setTooltipPosition] = useState<Position>(undefined);

    useEffect(() => {
        if (!isHidden && ref?.current) {
            const target = positionElementClassName
                ? ref.current.getElementsByClassName(positionElementClassName)[0]
                : ref.current;

            const onMouseOver = () => {
                const pos = getElementCoordinatesBySelector(target);

                const widthCenterShift =
                    (direction.startsWith('up') || direction.startsWith('down')) &&
                    direction.toLowerCase().includes('center')
                        ? (pos?.width || 0) / 2
                        : 0;
                const heightCenterShift =
                    (direction.startsWith('left') || direction.startsWith('right')) &&
                    direction.toLowerCase().includes('center')
                        ? (pos?.height || 0) / 2
                        : 0;

                const widthRight = direction.startsWith('right') ? pos?.width || 0 : 0;
                const heightDown = direction.startsWith('down') ? pos?.height || 0 : 0;

                setTooltipPosition({
                    y: (pos?.y || 0) + heightCenterShift + heightDown,
                    x: (pos?.x || 0) + widthCenterShift + widthRight,
                });
            };

            const onMouseOut = () => {
                setTooltipPosition(undefined);
            };

            if (isConfirm || isPermanentlyVisible) {
                onMouseOver();
            } else {
                target?.addEventListener('mouseover', onMouseOver);
                target?.addEventListener('mouseout', onMouseOut);
            }

            return () => {
                target?.removeEventListener('mouseover', onMouseOver);
                target?.removeEventListener('mouseout', onMouseOut);
            };
        }

        return undefined;
    }, [direction, isConfirm, isHidden, isPermanentlyVisible, positionElementClassName, ref]);

    return (
        <div ref={ref} className={`${!isInlineTable ? Style.toolTipWrapper : ''} ${className || ''}`} style={style}>
            {children}
            {!isHidden && (
                <Tooltip
                    size={size}
                    position={tooltipPosition}
                    direction={direction}
                    isInteractive={isInteractive}
                    className={toolTipClassName}
                    style={toolTipStyle}
                    status={status}
                >
                    {isConfirm ? (
                        <div className={Style.toolTipWrapperConfirm}>
                            <div className={Style.toolTipWrapperConfirmMessage}>{message}</div>
                            <div className={Style.toolTipWrapperConfirmButtons}>
                                <Button onClick={onConfirm} size="s" label={confirmLabel} />
                                <Button onClick={onCancel} size="s" label={cancelLabel} />
                            </div>
                        </div>
                    ) : (
                        message
                    )}
                </Tooltip>
            )}
        </div>
    );
};

TooltipWrapper.displayName = 'TooltipWrapper';
